import { SearchContext } from "src/search/useSearchContext";
import { Field } from "src/search/types";
import { Api } from "@vzbl/integrations";
import { VZBLTooltip } from "../tooltips/VZBLTooltips";
import { FilterTag } from "src/search/components/inputs/FilterTag";
import { formatValue } from "src/utils/strings";
import { TimeUnit } from "../FormattingControls";
import { Avatar } from "antd";
import { asKey } from "src/utils/fields";

interface UseMeasureTableValueProps {
  context: SearchContext;
  field: Field;
  value: Api.FieldValue;
  columnInfo: Api.Schema.ColumnInfo;
  timeUnit?: TimeUnit;
}

export function useMeasureTableValue({
  context,
  field,
  value,
  columnInfo,
  timeUnit,
}: UseMeasureTableValueProps) {
  const propsForValue = (value: Api.FieldValue) => {
    const columnMetadata = columnInfo?.find((c) => c.key == asKey(field)) || {
      type: "string",
      unit: undefined,
      ...field,
    };

    if (value && typeof value == "object") {
      const label = value.value;

      if (!label || label === "__VZBL::null__") {
        return {
          properties: [field],
          values: [{ label: "", value: value.value || "" }],
          metadata: {
            type: columnMetadata.type,
            unit: columnMetadata.unit,
            target: columnMetadata.target,
            attribute: columnMetadata.attribute,
            isEntity: true,
            entityType: field.target,
          },
        };
      }
      const labelStr = String(label);
      return {
        properties: [field],
        values: [{ label: labelStr, value: value.value }],
        entities:
          value["uuid"] === "__VZBL::null__" ? [] : [{ uuid: value.uuid }],
        icon: value["avatarUrl"] ? (
          <Avatar
            shape="square"
            size="small"
            src={
              value.avatarUrl && value.avatarUrl !== "__VZBL::null__"
                ? value.avatarUrl
                : undefined
            }
            style={{ width: 20, height: 20 }}
          >
            {labelStr
              .split(" ")
              .map((name: string) => name[0]?.toLocaleUpperCase() || "")
              .join("")}
          </Avatar>
        ) : undefined,
        metadata: {
          type: columnMetadata.type,
          unit: columnMetadata.unit,
          target: columnMetadata.target,
          attribute: columnMetadata.attribute,
          isEntity: true,
          entityType: field.target,
          entityId: value.uuid,
          entityAvatar: value.avatarUrl,
        },
      };
    } else {
      const ret = { label: `${value || ""}`, value: `${value}` };
      ret.label = formatValue(
        value,
        columnMetadata.type,
        columnMetadata.unit,
        timeUnit
      );

      return {
        properties: [field],
        values: [ret],
        metadata: {
          type: columnMetadata.type,
          unit: columnMetadata.unit,
          target: columnMetadata.target,
          attribute: columnMetadata.attribute,
          isEntity: false,
          rawValue: value,
          formattedValue: ret.label,
        },
      };
    }
  };

  const props = propsForValue(value);

  // Determine the type based on the value
  const cellType = value && typeof value === "object" ? "entity" : "dimension";

  // Remove icon from props to avoid duplication
  const { icon, ...filterTagProps } = props;

  const cellContent = (
    <FilterTag
      options={{
        showEntityOpener: true,
        showFilters: true,
        showGroupBy: true,
        showAggregate: false,
        showDrillDown: true,
        showValueCopiers: false,
      }}
      context={context}
      {...filterTagProps}
    />
  );

  return (
    <VZBLTooltip
      type={cellType}
      value={value}
      formattedValue={props.values[0].label}
      field={field}
      record={undefined}
      metadata={props.metadata}
      columnInfo={columnInfo}
      currentColumn={columnInfo.find((c) => c.key == asKey(field))}
      onClick={(e) => {
        // Handle click event if needed
      }}
    >
      {cellContent}
    </VZBLTooltip>
  );
}
